import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import client from "../api/client";
import { useSocket } from "../hooks/useSocket";
import Modal from "../components/Modal";
import DifficultyBadge from "../components/DifficultyBadge";
import { useToast } from "../contexts/ToastContext";

type Tab = "overview" | "challenges" | "users" | "submissions" | "anti-cheat" | "audit-log" | "watch";

interface Stats {
  users: number;
  challenges: number;
  submissions: number;
  teams: number;
  wrongAttempts: number;
}

interface TopChallenge {
  id: string;
  title: string;
  category: string;
  difficulty: "EASY" | "MEDIUM" | "HARD" | "INSANE";
  submissionsCount: number;
}

interface RecentSubmission {
  id: string;
  username: string;
  challengeTitle: string;
  correct: boolean;
  createdAt: string;
}

interface OverviewData {
  counts: Stats;
  topChallenges: TopChallenge[];
  recentSubmissions: any[];
}

interface User {
  id: string;
  username: string;
  email: string;
  role: string;
  totalPoints: number;
  solvedCount: number;
  twoFactorEnabled: boolean;
  banned: boolean;
  createdAt: string;
}

interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface SubmissionFeedEntry {
  id: string;
  username: string;
  challengeTitle: string;
  correct: boolean;
  createdAt: string;
}

interface AntiCheatAlert {
  id: string;
  user: { id: string; username: string };
  challenge: { id: string; title: string };
  reason: string;
  severity: "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";
  createdAt: string;
  isResolved: boolean;
}

interface AuditLogEntry {
  id: string;
  createdAt: string;
  action: string;
  user: { id: string; username: string } | string;
  resource: string;
  details: Record<string, unknown>;
  ip: string;
}

interface Challenge {
  id: string;
  title: string;
  description: string;
  category: string;
  difficulty: "EASY" | "MEDIUM" | "HARD" | "INSANE";
  flag: string;
  basePoints: number;
  currentPoints: number;
  tags: string[];
  solveCount: number;
  isActive: boolean;
  createdAt: string;
}

const CATEGORIES = ["Web", "Crypto", "Forensics", "Pwn", "Reversing", "Misc", "OSINT", "Hardware", "Blockchain", "Stego"];

const TABS: { key: Tab; label: string }[] = [
  { key: "overview", label: "Overview" },
  { key: "challenges", label: "Challenges" },
  { key: "users", label: "Users" },
  { key: "submissions", label: "Submissions" },
  { key: "anti-cheat", label: "Anti-Cheat" },
  { key: "audit-log", label: "Audit Log" },
  { key: "watch", label: "Watch" },
];

const ROLES = ["PLAYER", "AUTHOR", "ORGANIZER", "SUPER_ADMIN"] as const;

const ROLE_STYLES: Record<string, string> = {
  SUPER_ADMIN: "text-ds-danger bg-ds-danger/10 border border-ds-danger/20",
  ORGANIZER: "text-ds-accent bg-ds-accent/10 border border-ds-accent/20",
  AUTHOR: "text-ds-success bg-ds-success/10 border border-ds-success/20",
  PLAYER: "text-ds-text-dim bg-ds-surface border border-ds-border",
};

const SEVERITY_STYLES: Record<string, string> = {
  LOW: "text-ds-text-muted",
  MEDIUM: "text-ds-warning",
  HIGH: "text-ds-coral",
  CRITICAL: "text-ds-danger animate-pulse",
};

const ACTION_LABELS: Record<string, string> = {
  "auth:login": "Login",
  "auth:logout": "Logout",
  "auth:register": "Registration",
  "auth:2fa:enable": "2FA Enabled",
  "auth:2fa:disable": "2FA Disabled",
  "flag:submit": "Flag Submission",
  "challenge:create": "Challenge Created",
  "challenge:update": "Challenge Updated",
  "challenge:delete": "Challenge Deleted",
  "user:ban": "User Banned",
  "user:unban": "User Unbanned",
  "user:role:change": "Role Changed",
  "team:create": "Team Created",
  "team:join": "Team Joined",
  "anti-cheat:alert": "Anti-Cheat Alert",
};

function formatDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatAction(raw: string) {
  return ACTION_LABELS[raw] || raw;
}

export default function Admin() {
  const { addToast } = useToast();
  const { on, off } = useSocket();

  const [activeTab, setActiveTab] = useState<Tab>("overview");

  const [overview, setOverview] = useState<OverviewData | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(1);
  const [usersTotalPages, setUsersTotalPages] = useState(1);
  const [userSearch, setUserSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");

  const [feed, setFeed] = useState<SubmissionFeedEntry[]>([]);

  const [alerts, setAlerts] = useState<AntiCheatAlert[]>([]);

  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);
  const [auditPage, setAuditPage] = useState(1);
  const [auditTotalPages, setAuditTotalPages] = useState(1);

  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [challengesLoading, setChallengesLoading] = useState(false);
  const [challengeFormOpen, setChallengeFormOpen] = useState(false);
  const [editingChallenge, setEditingChallenge] = useState<Challenge | null>(null);
  const [challengeForm, setChallengeForm] = useState({
    title: "",
    description: "",
    category: "Web",
    difficulty: "EASY",
    flag: "",
    basePoints: 500,
    tags: "",
    hints: [{ level: 1, content: "", cost: 10 }] as { level: number; content: string; cost: number }[],
  });
  const [challengeSaving, setChallengeSaving] = useState(false);

  const [watchVideos, setWatchVideos] = useState<any[]>([]);
  const [watchLoading, setWatchLoading] = useState(false);
  const [watchFormOpen, setWatchFormOpen] = useState(false);
  const [watchForm, setWatchForm] = useState({ title: "", description: "", youtubeUrl: "", sortOrder: 0 });
  const [watchSaving, setWatchSaving] = useState(false);

  const [loading, setLoading] = useState(true);

  const [banModal, setBanModal] = useState<{ open: boolean; userId: string; username: string }>({
    open: false,
    userId: "",
    username: "",
  });
  const [banReason, setBanReason] = useState("");
  const [banLoading, setBanLoading] = useState(false);

  const [roleEditId, setRoleEditId] = useState<string | null>(null);

  const fetchOverview = useCallback(async () => {
    try {
      const res = await client.get("/admin/stats");
      setOverview(res.data.data);
    } catch {
      addToast("error", "Failed to load overview stats");
    }
  }, [addToast]);

  const fetchUsers = useCallback(async (page: number, search: string) => {
    try {
      const params: Record<string, string | number> = { page, limit: 20 };
      if (search) params.search = search;
      const res = await client.get("/admin/users", { params });
      const payload = res.data?.data;
      const userList = payload?.data ?? [];
      const pagination = payload?.pagination ?? {};
      setUsers(userList);
      setUsersTotal(pagination.total ?? 0);
      setUsersPage(pagination.page ?? 1);
      setUsersTotalPages(pagination.totalPages ?? 1);
    } catch {
      addToast("error", "Failed to load users");
    }
  }, [addToast]);

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await client.get("/admin/anti-cheat");
      setAlerts(res.data.data);
    } catch {
      addToast("error", "Failed to load anti-cheat alerts");
    }
  }, [addToast]);

  const fetchAuditLogs = useCallback(async (page: number) => {
    try {
      const res = await client.get("/admin/audit-logs", {
        params: { page, limit: 20 },
      });
      const payload = res.data?.data;
      const logList = payload?.data ?? [];
      const pagination = payload?.pagination ?? {};
      setAuditLogs(logList);
      setAuditTotalPages(pagination.totalPages ?? 1);
      setAuditPage(pagination.page ?? 1);
    } catch {
      addToast("error", "Failed to load audit logs");
    }
  }, [addToast]);

  const fetchChallenges = useCallback(async () => {
    setChallengesLoading(true);
    try {
      const res = await client.get("/challenges");
      const payload = res.data?.data;
      const list = payload?.data ?? (Array.isArray(payload) ? payload : []);
      setChallenges(list);
    } catch {
      addToast("error", "Failed to load challenges");
    } finally {
      setChallengesLoading(false);
    }
  }, [addToast]);

  const fetchWatchVideos = useCallback(async () => {
    setWatchLoading(true);
    try {
      const res = await client.get("/watch/videos/all");
      setWatchVideos(res.data?.data ?? []);
    } catch {
      addToast("error", "Failed to load watch videos");
    } finally {
      setWatchLoading(false);
    }
  }, [addToast]);

  const handleChallengeSubmit = async () => {
    if (!challengeForm.title.trim() || !challengeForm.flag.trim()) {
      addToast("error", "Title and flag are required");
      return;
    }
    setChallengeSaving(true);
    try {
      const body = {
        title: challengeForm.title,
        description: challengeForm.description,
        category: challengeForm.category,
        difficulty: challengeForm.difficulty,
        flag: challengeForm.flag,
        basePoints: challengeForm.basePoints,
        tags: challengeForm.tags.split(",").map((t) => t.trim()).filter(Boolean),
        hints: challengeForm.hints.filter((h) => h.content.trim()),
      };
      if (editingChallenge) {
        await client.put(`/challenges/${editingChallenge.id}`, body);
        addToast("success", "Challenge updated");
      } else {
        await client.post("/challenges", body);
        addToast("success", "Challenge created");
      }
      setChallengeFormOpen(false);
      setEditingChallenge(null);
      setChallengeForm({ title: "", description: "", category: "Web", difficulty: "EASY", flag: "", basePoints: 500, tags: "", hints: [{ level: 1, content: "", cost: 10 }] });
      fetchChallenges();
    } catch {
      addToast("error", editingChallenge ? "Failed to update challenge" : "Failed to create challenge");
    } finally {
      setChallengeSaving(false);
    }
  };

  const handleChallengeDelete = async (id: string, title: string) => {
    if (!confirm(`Delete challenge "${title}"?`)) return;
    try {
      await client.delete(`/challenges/${id}`);
      addToast("success", "Challenge deleted");
      fetchChallenges();
    } catch {
      addToast("error", "Failed to delete challenge");
    }
  };

  const startEditChallenge = async (c: Challenge) => {
    setEditingChallenge(c);
    // Fetch full challenge with hints
    try {
      const res = await client.get(`/challenges/${c.id}`);
      const full = res.data?.data ?? res.data;
      setChallengeForm({
        title: c.title,
        description: c.description,
        category: c.category,
        difficulty: c.difficulty,
        flag: c.flag,
        basePoints: c.basePoints,
        tags: c.tags.join(", "),
        hints: full.hints && full.hints.length > 0
          ? full.hints.map((h: any) => ({ level: h.level, content: h.content || "", cost: h.cost }))
          : [{ level: 1, content: "", cost: 10 }],
      });
    } catch {
      setChallengeForm({
        title: c.title,
        description: c.description,
        category: c.category,
        difficulty: c.difficulty,
        flag: c.flag,
        basePoints: c.basePoints,
        tags: c.tags.join(", "),
        hints: [{ level: 1, content: "", cost: 10 }],
      });
    }
    setChallengeFormOpen(true);
  };

  const handleWatchSubmit = async () => {
    if (!watchForm.title.trim() || !watchForm.youtubeUrl.trim()) {
      addToast("error", "Title and YouTube URL are required");
      return;
    }
    setWatchSaving(true);
    try {
      await client.post("/watch/videos", watchForm);
      addToast("success", "Video qo'shildi");
      setWatchFormOpen(false);
      setWatchForm({ title: "", description: "", youtubeUrl: "", sortOrder: 0 });
      fetchWatchVideos();
    } catch (err: any) {
      addToast("error", err?.response?.data?.error || "Failed to add video");
    } finally {
      setWatchSaving(false);
    }
  };

  const handleWatchDelete = async (id: string, title: string) => {
    if (!confirm(`Delete video "${title}"?`)) return;
    try {
      await client.delete(`/watch/videos/${id}`);
      addToast("success", "Video o'chirildi");
      fetchWatchVideos();
    } catch {
      addToast("error", "Failed to delete video");
    }
  };

  const handleWatchToggle = async (id: string, isActive: boolean) => {
    try {
      await client.put(`/watch/videos/${id}`, { isActive: !isActive });
      addToast("success", isActive ? "Video o'chirildi" : "Video yoqildi");
      fetchWatchVideos();
    } catch {
      addToast("error", "Failed to update video");
    }
  };

  useEffect(() => {
    if (activeTab === "overview") {
      setLoading(true);
      fetchOverview().finally(() => setLoading(false));
    }
  }, [activeTab, fetchOverview]);

  useEffect(() => {
    if (activeTab === "users") {
      setLoading(true);
      fetchUsers(usersPage, searchDebounced).finally(() => setLoading(false));
    }
  }, [activeTab, usersPage, searchDebounced, fetchUsers]);

  useEffect(() => {
    if (activeTab === "anti-cheat") {
      setLoading(true);
      fetchAlerts().finally(() => setLoading(false));
    }
  }, [activeTab, fetchAlerts]);

  useEffect(() => {
    if (activeTab === "audit-log") {
      setLoading(true);
      fetchAuditLogs(auditPage).finally(() => setLoading(false));
    }
  }, [activeTab, auditPage, fetchAuditLogs]);

  useEffect(() => {
    if (activeTab === "challenges") {
      fetchChallenges();
    }
  }, [activeTab, fetchChallenges]);

  useEffect(() => {
    if (activeTab === "watch") {
      fetchWatchVideos();
    }
  }, [activeTab, fetchWatchVideos]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setUsersPage(1);
      setSearchDebounced(userSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [userSearch]);

  useEffect(() => {
    const handler = (entry: SubmissionFeedEntry) => {
      setFeed((prev) => [entry, ...prev].slice(0, 100));
    };
    on("submission:feed", handler);
    return () => off("submission:feed", handler);
  }, [on, off]);

  const handleBan = async () => {
    if (!banReason.trim()) return;
    setBanLoading(true);
    try {
      await client.post(`/admin/users/${banModal.userId}/ban`, { reason: banReason });
      addToast("success", `User ${banModal.username} banned`);
      setBanModal({ open: false, userId: "", username: "" });
      setBanReason("");
      fetchUsers(usersPage, searchDebounced);
    } catch {
      addToast("error", "Failed to ban user");
    } finally {
      setBanLoading(false);
    }
  };

  const handleUnban = async (userId: string, username: string) => {
    try {
      await client.post(`/admin/users/${userId}/unban`);
      addToast("success", `User ${username} unbanned`);
      fetchUsers(usersPage, searchDebounced);
    } catch {
      addToast("error", "Failed to unban user");
    }
  };

  const handleRoleChange = async (userId: string, role: string) => {
    try {
      await client.put(`/admin/users/${userId}/role`, { role });
      addToast("success", "Role updated");
      setRoleEditId(null);
      fetchUsers(usersPage, searchDebounced);
    } catch {
      addToast("error", "Failed to change role");
    }
  };

  const handleResolve = async (alertId: string) => {
    try {
      await client.post(`/admin/anti-cheat/${alertId}/resolve`);
      addToast("success", "Alert resolved");
      setAlerts((prev) =>
        prev.map((a) => (a.id === alertId ? { ...a, isResolved: true } : a))
      );
    } catch {
      addToast("error", "Failed to resolve alert");
    }
  };

  const tabVariants = {
    hidden: { opacity: 0, y: 8 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -8 },
  };

  return (
    <div className="ds-page">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-ds-text">Admin Panel</h1>
          <p className="mt-1 text-sm text-ds-text-muted">Manage users, submissions, and platform integrity</p>
        </div>
        <Link
          to="/admin/effects"
          className="ds-btn-ghost flex items-center gap-2 border border-ds-border text-xs hover:border-ds-accent/40"
        >
          <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="1" y="3" width="14" height="10" rx="1" />
            <circle cx="8" cy="8" r="2.5" />
          </svg>
          Visual Effects
        </Link>
      </div>

      <nav className="mb-6 flex gap-1 border-b border-ds-border">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`relative px-4 py-2.5 text-sm font-medium transition-colors ${
              activeTab === tab.key
                ? "text-ds-accent"
                : "text-ds-text-muted hover:text-ds-text-dim"
            }`}
          >
            {tab.label}
            {activeTab === tab.key && (
              <motion.div
                layoutId="admin-tab-indicator"
                className="absolute bottom-0 left-0 right-0 h-0.5 bg-ds-accent"
                transition={{ type: "spring", stiffness: 400, damping: 30 }}
              />
            )}
          </button>
        ))}
      </nav>

      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          variants={tabVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          transition={{ duration: 0.15 }}
        >
          {activeTab === "overview" && (
            <OverviewTab
              data={overview}
              loading={loading}
            />
          )}

          {activeTab === "challenges" && (
            <ChallengesTab
              challenges={challenges}
              loading={challengesLoading}
              formOpen={challengeFormOpen}
              editingChallenge={editingChallenge}
              form={challengeForm}
              saving={challengeSaving}
              onFormOpen={() => { setChallengeFormOpen(true); setEditingChallenge(null); setChallengeForm({ title: "", description: "", category: "Web", difficulty: "EASY", flag: "", basePoints: 500, tags: "", hints: [{ level: 1, content: "", cost: 10 }] }); }}
              onFormClose={() => { setChallengeFormOpen(false); setEditingChallenge(null); }}
              onFormChange={setChallengeForm}
              onSubmit={handleChallengeSubmit}
              onEdit={startEditChallenge}
              onDelete={handleChallengeDelete}
            />
          )}

          {activeTab === "users" && (
            <UsersTab
              users={users}
              total={usersTotal}
              page={usersPage}
              totalPages={usersTotalPages}
              search={userSearch}
              loading={loading}
              onSearch={setUserSearch}
              onPageChange={setUsersPage}
              roleEditId={roleEditId}
              onRoleEditStart={setRoleEditId}
              onRoleChange={handleRoleChange}
              onBan={(id, name) => setBanModal({ open: true, userId: id, username: name })}
              onUnban={handleUnban}
            />
          )}

          {activeTab === "submissions" && (
            <SubmissionsTab feed={feed} />
          )}

          {activeTab === "anti-cheat" && (
            <AntiCheatTab
              alerts={alerts}
              loading={loading}
              onResolve={handleResolve}
            />
          )}

          {activeTab === "audit-log" && (
            <AuditLogTab
              logs={auditLogs}
              page={auditPage}
              totalPages={auditTotalPages}
              loading={loading}
              onPageChange={setAuditPage}
            />
          )}

          {activeTab === "watch" && (
            <WatchTab
              videos={watchVideos}
              loading={watchLoading}
              formOpen={watchFormOpen}
              form={watchForm}
              saving={watchSaving}
              onFormOpen={() => { setWatchFormOpen(true); setWatchForm({ title: "", description: "", youtubeUrl: "", sortOrder: 0 }); }}
              onFormClose={() => setWatchFormOpen(false)}
              onFormChange={setWatchForm}
              onSubmit={handleWatchSubmit}
              onDelete={handleWatchDelete}
              onToggle={handleWatchToggle}
            />
          )}
        </motion.div>
      </AnimatePresence>

      <Modal
        isOpen={banModal.open}
        onClose={() => { setBanModal({ open: false, userId: "", username: "" }); setBanReason(""); }}
        title={`Ban ${banModal.username}`}
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-ds-text-dim">
            This user will be unable to log in or submit flags.
          </p>
          <div>
            <label className="block text-xs font-medium text-ds-text-dim mb-1.5">Reason</label>
            <textarea
              className="ds-input min-h-[80px] resize-none"
              placeholder="Enter ban reason..."
              value={banReason}
              onChange={(e) => setBanReason(e.target.value)}
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              className="ds-btn-ghost text-xs"
              onClick={() => { setBanModal({ open: false, userId: "", username: "" }); setBanReason(""); }}
            >
              Cancel
            </button>
            <button
              className="ds-btn-danger text-xs"
              disabled={!banReason.trim() || banLoading}
              onClick={handleBan}
            >
              {banLoading ? "Banning..." : "Ban User"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function OverviewTab({ data, loading }: { data: OverviewData | null; loading: boolean }) {
  if (loading && !data) {
    return <LoadingState />;
  }

  if (!data) {
    return <EmptyState text="No overview data available" />;
  }

  const statCards = [
    { label: "Users", value: data.counts.users },
    { label: "Challenges", value: data.counts.challenges },
    { label: "Correct Submissions", value: data.counts.submissions },
    { label: "Teams", value: data.counts.teams },
    { label: "Wrong Attempts", value: data.counts.wrongAttempts },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {statCards.map((card) => (
          <div key={card.label} className="ds-card p-4">
            <p className="text-xs font-medium text-ds-text-muted uppercase tracking-wider">
              {card.label}
            </p>
            <p className="mt-2 font-mono text-3xl font-bold text-ds-text">
              {card.value.toLocaleString()}
            </p>
          </div>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="ds-card overflow-hidden">
          <div className="border-b border-ds-border px-4 py-3">
            <h3 className="text-sm font-semibold text-ds-text">Top Challenges</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ds-border text-left">
                  <th className="px-4 py-2.5 text-xs font-medium text-ds-text-muted">Title</th>
                  <th className="px-4 py-2.5 text-xs font-medium text-ds-text-muted">Category</th>
                  <th className="px-4 py-2.5 text-xs font-medium text-ds-text-muted">Difficulty</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium text-ds-text-muted">Solves</th>
                </tr>
              </thead>
              <tbody>
                {data.topChallenges.map((c) => (
                  <tr key={c.id} className="border-b border-ds-border/50 last:border-0">
                    <td className="px-4 py-2.5 text-ds-text">{c.title}</td>
                    <td className="px-4 py-2.5 text-ds-text-dim">{c.category}</td>
                    <td className="px-4 py-2.5">
                      <DifficultyBadge difficulty={c.difficulty} />
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-ds-text-dim">
                      {c.submissionsCount}
                    </td>
                  </tr>
                ))}
                {data.topChallenges.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-ds-text-muted">No challenges yet</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="ds-card overflow-hidden">
          <div className="border-b border-ds-border px-4 py-3">
            <h3 className="text-sm font-semibold text-ds-text">Recent Submissions</h3>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {data.recentSubmissions.map((s: any) => (
              <div
                key={s.id}
                className="flex items-center gap-3 border-b border-ds-border/50 px-4 py-2.5 last:border-0"
              >
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs ${
                    s.isCorrect
                      ? "bg-ds-success/15 text-ds-success"
                      : "bg-ds-danger/15 text-ds-danger"
                  }`}
                >
                  {s.isCorrect ? "\u2713" : "\u2717"}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-ds-text">
                    <span className="font-medium">{s.user?.username ?? "Unknown"}</span>
                    <span className="text-ds-text-muted"> &rarr; </span>
                    <span className="text-ds-text-dim">{s.challenge?.title ?? "Unknown"}</span>
                  </p>
                </div>
                <span className="shrink-0 text-xs text-ds-text-muted font-mono">
                  {formatDate(s.submittedAt)}
                </span>
              </div>
            ))}
            {data.recentSubmissions.length === 0 && (
              <p className="px-4 py-8 text-center text-sm text-ds-text-muted">No submissions yet</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface ChallengesTabProps {
  challenges: Challenge[];
  loading: boolean;
  formOpen: boolean;
  editingChallenge: Challenge | null;
  form: { title: string; description: string; category: string; difficulty: string; flag: string; basePoints: number; tags: string; hints: { level: number; content: string; cost: number }[] };
  saving: boolean;
  onFormOpen: () => void;
  onFormClose: () => void;
  onFormChange: (form: any) => void;
  onSubmit: () => void;
  onEdit: (c: Challenge) => void;
  onDelete: (id: string, title: string) => void;
}

function ChallengesTab({
  challenges,
  loading,
  formOpen,
  editingChallenge,
  form,
  saving,
  onFormOpen,
  onFormClose,
  onFormChange,
  onSubmit,
  onEdit,
  onDelete,
}: ChallengesTabProps) {
  const DIFFICULTY_STYLES: Record<string, string> = {
    EASY: "text-ds-success bg-ds-success/10 border border-ds-success/20",
    MEDIUM: "text-ds-warning bg-ds-warning/10 border border-ds-warning/20",
    HARD: "text-ds-coral bg-ds-coral/10 border border-ds-coral/20",
    INSANE: "text-ds-danger bg-ds-danger/10 border border-ds-danger/20",
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-xs text-ds-text-muted font-mono">
          {challenges.length} challenges
        </span>
        <button onClick={onFormOpen} className="ds-btn-primary text-xs">
          + New Challenge
        </button>
      </div>

      {loading ? (
        <LoadingState />
      ) : challenges.length === 0 ? (
        <EmptyState text="No challenges yet" />
      ) : (
        <div className="ds-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ds-border text-left">
                  <th className="px-4 py-2.5 text-xs font-medium text-ds-text-muted">Title</th>
                  <th className="px-4 py-2.5 text-xs font-medium text-ds-text-muted">Category</th>
                  <th className="px-4 py-2.5 text-xs font-medium text-ds-text-muted">Difficulty</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium text-ds-text-muted">Points</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium text-ds-text-muted">Solves</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium text-ds-text-muted">Actions</th>
                </tr>
              </thead>
              <tbody>
                {challenges.map((c) => (
                  <tr key={c.id} className="border-b border-ds-border/50 last:border-0 hover:bg-ds-surface/50 transition-colors">
                    <td className="px-4 py-2.5 font-medium text-ds-text">{c.title}</td>
                    <td className="px-4 py-2.5">
                      <span className="ds-badge text-ds-accent bg-ds-accent/10 border border-ds-accent/20">
                        {c.category}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`ds-badge ${DIFFICULTY_STYLES[c.difficulty] || ""}`}>
                        {c.difficulty}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-ds-text">{c.basePoints}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-ds-text-muted">
                      {c.solveCount ?? 0}
                    </td>
                    <td className="px-4 py-2.5 text-right space-x-2">
                      <button
                        onClick={() => onEdit(c)}
                        className="ds-btn-ghost px-2 py-1 text-xs text-ds-accent hover:text-ds-accent-hover"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => onDelete(c.id, c.title)}
                        className="px-2 py-1 text-xs text-ds-danger hover:underline"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Modal
        isOpen={formOpen}
        onClose={onFormClose}
        title={editingChallenge ? "Edit Challenge" : "New Challenge"}
        size="lg"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-xs text-ds-text-muted mb-1">Title</label>
            <input
              className="ds-input w-full"
              value={form.title}
              onChange={(e) => onFormChange({ ...form, title: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-xs text-ds-text-muted mb-1">Description</label>
            <textarea
              className="ds-input w-full h-24 resize-none"
              value={form.description}
              onChange={(e) => onFormChange({ ...form, description: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-ds-text-muted mb-1">Category</label>
              <select
                className="ds-input w-full"
                value={form.category}
                onChange={(e) => onFormChange({ ...form, category: e.target.value })}
              >
                {CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-ds-text-muted mb-1">Difficulty</label>
              <select
                className="ds-input w-full"
                value={form.difficulty}
                onChange={(e) => onFormChange({ ...form, difficulty: e.target.value })}
              >
                {(["EASY", "MEDIUM", "HARD", "INSANE"] as const).map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs text-ds-text-muted mb-1">Flag</label>
            <input
              className="ds-input w-full font-mono"
              value={form.flag}
              onChange={(e) => onFormChange({ ...form, flag: e.target.value })}
              placeholder="flag{...}"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-ds-text-muted mb-1">Base Points</label>
              <input
                type="number"
                className="ds-input w-full"
                value={form.basePoints}
                onChange={(e) => onFormChange({ ...form, basePoints: Number(e.target.value) })}
              />
            </div>
            <div>
              <label className="block text-xs text-ds-text-muted mb-1">Tags (comma-separated)</label>
              <input
                className="ds-input w-full"
                value={form.tags}
                onChange={(e) => onFormChange({ ...form, tags: e.target.value })}
                placeholder="pwn, buffer-overflow"
              />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs text-ds-text-muted font-medium">Hints</label>
              <button
                type="button"
                className="text-xs text-ds-accent hover:text-ds-accent-hover"
                onClick={() => onFormChange({
                  ...form,
                  hints: [...form.hints, { level: form.hints.length + 1, content: "", cost: 10 }],
                })}
              >
                + Add Hint
              </button>
            </div>
            <div className="space-y-3">
              {form.hints.map((hint, idx) => (
                <div key={idx} className="rounded-lg border border-ds-border bg-ds-surface/50 p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-ds-text-muted font-mono">Hint {hint.level}</span>
                    {form.hints.length > 1 && (
                      <button
                        type="button"
                        className="text-xs text-ds-danger hover:underline"
                        onClick={() => onFormChange({
                          ...form,
                          hints: form.hints.filter((_, i) => i !== idx).map((h, i) => ({ ...h, level: i + 1 })),
                        })}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <textarea
                    className="ds-input w-full h-16 resize-none text-xs"
                    value={hint.content}
                    onChange={(e) => {
                      const updated = [...form.hints];
                      updated[idx] = { ...updated[idx], content: e.target.value };
                      onFormChange({ ...form, hints: updated });
                    }}
                    placeholder="Hint content..."
                  />
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-ds-text-muted">Cost:</label>
                    <input
                      type="number"
                      className="ds-input w-20 text-xs"
                      value={hint.cost}
                      min={0}
                      max={500}
                      onChange={(e) => {
                        const updated = [...form.hints];
                        updated[idx] = { ...updated[idx], cost: Number(e.target.value) };
                        onFormChange({ ...form, hints: updated });
                      }}
                    />
                    <span className="text-xs text-ds-text-muted">pts</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2 border-t border-ds-border">
            <button onClick={onFormClose} className="ds-btn-ghost text-xs">
              Cancel
            </button>
            <button
              onClick={onSubmit}
              disabled={saving}
              className="ds-btn-primary text-xs"
            >
              {saving ? "Saving..." : editingChallenge ? "Update" : "Create"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

interface UsersTabProps {
  users: User[];
  total: number;
  page: number;
  totalPages: number;
  search: string;
  loading: boolean;
  onSearch: (value: string) => void;
  onPageChange: (page: number) => void;
  roleEditId: string | null;
  onRoleEditStart: (id: string | null) => void;
  onRoleChange: (userId: string, role: string) => void;
  onBan: (userId: string, username: string) => void;
  onUnban: (userId: string, username: string) => void;
}

function UsersTab({
  users,
  total,
  page,
  totalPages,
  search,
  loading,
  onSearch,
  onPageChange,
  roleEditId,
  onRoleEditStart,
  onRoleChange,
  onBan,
  onUnban,
}: UsersTabProps) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <div className="relative max-w-sm flex-1">
          <svg
            className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ds-text-muted"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            className="ds-input pl-10"
            placeholder="Search users..."
            value={search}
            onChange={(e) => onSearch(e.target.value)}
          />
        </div>
        <span className="text-xs text-ds-text-muted font-mono">{total} total</span>
      </div>

      {loading ? (
        <LoadingState />
      ) : (
        <div className="ds-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ds-border text-left">
                  <th className="px-4 py-2.5 text-xs font-medium text-ds-text-muted">Username</th>
                  <th className="px-4 py-2.5 text-xs font-medium text-ds-text-muted">Email</th>
                  <th className="px-4 py-2.5 text-xs font-medium text-ds-text-muted">Role</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium text-ds-text-muted">Points</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium text-ds-text-muted">Solved</th>
                  <th className="px-4 py-2.5 text-center text-xs font-medium text-ds-text-muted">2FA</th>
                  <th className="px-4 py-2.5 text-center text-xs font-medium text-ds-text-muted">Status</th>
                  <th className="px-4 py-2.5 text-xs font-medium text-ds-text-muted">Joined</th>
                  <th className="px-4 py-2.5 text-xs font-medium text-ds-text-muted text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-b border-ds-border/50 last:border-0 hover:bg-ds-surface/50 transition-colors">
                    <td className="px-4 py-2.5 font-medium text-ds-text">{user.username}</td>
                    <td className="px-4 py-2.5 text-ds-text-dim text-xs">{user.email}</td>
                    <td className="px-4 py-2.5">
                      {roleEditId === user.id ? (
                        <select
                          className="ds-input py-1 px-2 text-xs"
                          defaultValue={user.role}
                          autoFocus
                          onBlur={() => onRoleEditStart(null)}
                          onChange={(e) => onRoleChange(user.id, e.target.value)}
                        >
                          {ROLES.map((r) => (
                            <option key={r} value={r}>{r}</option>
                          ))}
                        </select>
                      ) : (
                        <span className={`ds-badge ${ROLE_STYLES[user.role] || ROLE_STYLES.PLAYER}`}>
                          {user.role}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-ds-text">{user.totalPoints}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-ds-text-dim">{user.solvedCount}</td>
                    <td className="px-4 py-2.5 text-center">
                      {user.twoFactorEnabled ? (
                        <span className="text-ds-success" title="2FA enabled">&#9679;</span>
                      ) : (
                        <span className="text-ds-text-muted" title="2FA disabled">&#9675;</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      {user.banned ? (
                        <span className="ds-badge text-ds-danger bg-ds-danger/10 border border-ds-danger/20">Banned</span>
                      ) : (
                        <span className="ds-badge text-ds-success bg-ds-success/10 border border-ds-success/20">Active</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-ds-text-muted font-mono">{formatDate(user.createdAt)}</td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          className="ds-btn-ghost px-2 py-1 text-xs"
                          onClick={() => onRoleEditStart(roleEditId === user.id ? null : user.id)}
                          title="Change Role"
                        >
                          Role
                        </button>
                        {user.banned ? (
                          <button
                            className="ds-btn-ghost px-2 py-1 text-xs text-ds-success hover:text-ds-success"
                            onClick={() => onUnban(user.id, user.username)}
                            title="Unban user"
                          >
                            Unban
                          </button>
                        ) : (
                          <button
                            className="ds-btn-ghost px-2 py-1 text-xs text-ds-danger hover:text-ds-danger"
                            onClick={() => onBan(user.id, user.username)}
                            title="Ban user"
                          >
                            Ban
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center text-ds-text-muted">No users found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {totalPages > 1 && (
        <Pagination page={page} totalPages={totalPages} onPageChange={onPageChange} />
      )}
    </div>
  );
}

function SubmissionsTab({ feed }: { feed: SubmissionFeedEntry[] }) {
  const [flashIds, setFlashIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (feed.length === 0) return;
    const newest = feed[0];
    if (newest.correct) {
      setFlashIds((prev) => new Set(prev).add(newest.id));
      const timer = setTimeout(() => {
        setFlashIds((prev) => {
          const next = new Set(prev);
          next.delete(newest.id);
          return next;
        });
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [feed]);

  return (
    <div className="space-y-4">
      <div className="ds-card overflow-hidden">
        <div className="flex items-center justify-between border-b border-ds-border px-4 py-3">
          <h3 className="text-sm font-semibold text-ds-text">Live Submission Feed</h3>
          <span className="flex items-center gap-1.5 text-xs text-ds-success">
            <span className="h-1.5 w-1.5 rounded-full bg-ds-success animate-pulse" />
            Connected
          </span>
        </div>

        {feed.length === 0 ? (
          <div className="px-4 py-16 text-center">
            <p className="text-sm text-ds-text-muted">Waiting for submissions...</p>
          </div>
        ) : (
          <div className="max-h-[600px] overflow-y-auto">
            <AnimatePresence initial={false}>
              {feed.map((entry) => (
                <motion.div
                  key={entry.id}
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <div
                    className={`flex items-center gap-3 border-b border-ds-border/50 px-4 py-2.5 last:border-0 transition-colors duration-500 ${
                      flashIds.has(entry.id)
                        ? "bg-ds-success/[0.08]"
                        : entry.correct
                        ? ""
                        : "opacity-60"
                    }`}
                  >
                    <span
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs ${
                        entry.correct
                          ? "bg-ds-success/15 text-ds-success"
                          : "bg-ds-danger/15 text-ds-danger"
                      }`}
                    >
                      {entry.correct ? "\u2713" : "\u2717"}
                    </span>
                    <div className="min-w-0 flex-1">
                      <span className="text-sm text-ds-text">
                        <span className="font-medium">{entry.username}</span>
                        <span className="text-ds-text-muted"> &rarr; </span>
                        <span className={entry.correct ? "text-ds-text" : "text-ds-text-muted"}>
                          {entry.challengeTitle}
                        </span>
                      </span>
                    </div>
                    <span className="shrink-0 text-xs text-ds-text-muted font-mono">
                      {formatDate(entry.createdAt)}
                    </span>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
}

function AntiCheatTab({
  alerts,
  loading,
  onResolve,
}: {
  alerts: AntiCheatAlert[];
  loading: boolean;
  onResolve: (id: string) => void;
}) {
  if (loading) {
    return <LoadingState />;
  }

  return (
    <div className="ds-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-ds-border text-left">
              <th className="px-4 py-2.5 text-xs font-medium text-ds-text-muted">User</th>
              <th className="px-4 py-2.5 text-xs font-medium text-ds-text-muted">Challenge</th>
              <th className="px-4 py-2.5 text-xs font-medium text-ds-text-muted">Reason</th>
              <th className="px-4 py-2.5 text-xs font-medium text-ds-text-muted">Severity</th>
              <th className="px-4 py-2.5 text-xs font-medium text-ds-text-muted">Time</th>
              <th className="px-4 py-2.5 text-xs font-medium text-ds-text-muted">Status</th>
              <th className="px-4 py-2.5 text-right text-xs font-medium text-ds-text-muted">Action</th>
            </tr>
          </thead>
          <tbody>
            {alerts.map((alert) => (
              <tr key={alert.id} className="border-b border-ds-border/50 last:border-0 hover:bg-ds-surface/50 transition-colors">
                <td className="px-4 py-2.5 font-medium text-ds-text">{alert.user?.username ?? "Unknown"}</td>
                <td className="px-4 py-2.5 text-ds-text-dim">{alert.challenge?.title ?? "Unknown"}</td>
                <td className="px-4 py-2.5 text-ds-text-dim text-xs max-w-xs truncate" title={alert.reason}>
                  {alert.reason}
                </td>
                <td className="px-4 py-2.5">
                  <span className={`text-xs font-medium font-mono ${SEVERITY_STYLES[alert.severity]}`}>
                    {alert.severity}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-xs text-ds-text-muted font-mono">{formatDate(alert.createdAt)}</td>
                <td className="px-4 py-2.5">
                  {alert.isResolved ? (
                    <span className="ds-badge text-ds-success bg-ds-success/10 border border-ds-success/20">Resolved</span>
                  ) : (
                    <span className="ds-badge text-ds-warning bg-ds-warning/10 border border-ds-warning/20">Open</span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-right">
                  {!alert.isResolved && (
                    <button
                      className="ds-btn-ghost px-2 py-1 text-xs text-ds-accent hover:text-ds-accent-hover"
                      onClick={() => onResolve(alert.id)}
                    >
                      Resolve
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {alerts.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-ds-text-muted">
                  No anti-cheat alerts
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AuditLogTab({
  logs,
  page,
  totalPages,
  loading,
  onPageChange,
}: {
  logs: AuditLogEntry[];
  page: number;
  totalPages: number;
  loading: boolean;
  onPageChange: (page: number) => void;
}) {
  if (loading) {
    return <LoadingState />;
  }

  return (
    <div className="space-y-4">
      <div className="ds-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ds-border text-left">
                <th className="px-4 py-2.5 text-xs font-medium text-ds-text-muted">Time</th>
                <th className="px-4 py-2.5 text-xs font-medium text-ds-text-muted">Action</th>
                <th className="px-4 py-2.5 text-xs font-medium text-ds-text-muted">User</th>
                <th className="px-4 py-2.5 text-xs font-medium text-ds-text-muted">Resource</th>
                <th className="px-4 py-2.5 text-xs font-medium text-ds-text-muted">Details</th>
                <th className="px-4 py-2.5 text-xs font-medium text-ds-text-muted">IP</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id} className="border-b border-ds-border/50 last:border-0 hover:bg-ds-surface/50 transition-colors">
                  <td className="px-4 py-2.5 text-xs text-ds-text-muted font-mono whitespace-nowrap">
                    {formatDate(log.createdAt)}
                  </td>
                  <td className="px-4 py-2.5">
                    <span className="ds-badge text-ds-accent bg-ds-accent/10 border border-ds-accent/20">
                      {formatAction(log.action)}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 font-medium text-ds-text">{typeof log.user === "object" ? log.user?.username ?? "System" : log.user || "System"}</td>
                  <td className="px-4 py-2.5 text-ds-text-dim text-xs">{log.resource}</td>
                  <td className="px-4 py-2.5">
                    <details className="group">
                      <summary className="cursor-pointer text-xs text-ds-text-muted hover:text-ds-text-dim">
                        {Object.keys(log.details).length > 0 ? "View" : "\u2014"}
                      </summary>
                      <pre className="mt-1 max-w-xs overflow-x-auto rounded bg-ds-surface border border-ds-border p-2 text-xs text-ds-text-dim font-mono">
                        {JSON.stringify(log.details, null, 2)}
                      </pre>
                    </details>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-ds-text-muted font-mono">{log.ip}</td>
                </tr>
              ))}
              {logs.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-ds-text-muted">
                    No audit logs
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <Pagination page={page} totalPages={totalPages} onPageChange={onPageChange} />
      )}
    </div>
  );
}

function Pagination({
  page,
  totalPages,
  onPageChange,
}: {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}) {
  return (
    <div className="flex items-center justify-center gap-1">
      <button
        className="ds-btn-ghost px-3 py-1.5 text-xs"
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
      >
        Prev
      </button>
      {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
        let pageNum: number;
        if (totalPages <= 7) {
          pageNum = i + 1;
        } else if (page <= 4) {
          pageNum = i + 1;
        } else if (page >= totalPages - 3) {
          pageNum = totalPages - 6 + i;
        } else {
          pageNum = page - 3 + i;
        }
        return (
          <button
            key={pageNum}
            className={`h-8 w-8 rounded text-xs font-mono transition-colors ${
              pageNum === page
                ? "bg-ds-accent text-white"
                : "text-ds-text-muted hover:text-ds-text hover:bg-ds-surface"
            }`}
            onClick={() => onPageChange(pageNum)}
          >
            {pageNum}
          </button>
        );
      })}
      <button
        className="ds-btn-ghost px-3 py-1.5 text-xs"
        disabled={page >= totalPages}
        onClick={() => onPageChange(page + 1)}
      >
        Next
      </button>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="ds-card flex items-center justify-center py-20">
      <div className="flex flex-col items-center gap-3">
        <div className="relative h-8 w-8">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="absolute inset-0 rounded-full border border-ds-accent/20"
              style={{
                animation: `radar-ping 2s ${i * 0.6}s cubic-bezier(0, 0, 0.2, 1) infinite`,
              }}
            />
          ))}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="h-1.5 w-1.5 rounded-full bg-ds-accent" />
          </div>
        </div>
        <p className="text-xs text-ds-text-muted">Loading...</p>
        <style>{`
          @keyframes radar-ping {
            0% { transform: scale(0.3); opacity: 0.8; }
            100% { transform: scale(1.4); opacity: 0; }
          }
        `}</style>
      </div>
    </div>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className="ds-card flex items-center justify-center py-20">
      <p className="text-sm text-ds-text-muted">{text}</p>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// Watch Tab
// ═══════════════════════════════════════════════════════════

function WatchTab({
  videos,
  loading,
  formOpen,
  form,
  saving,
  onFormOpen,
  onFormClose,
  onFormChange,
  onSubmit,
  onDelete,
  onToggle,
}: {
  videos: any[];
  loading: boolean;
  formOpen: boolean;
  form: { title: string; description: string; youtubeUrl: string; sortOrder: number };
  saving: boolean;
  onFormOpen: () => void;
  onFormClose: () => void;
  onFormChange: (f: any) => void;
  onSubmit: () => void;
  onDelete: (id: string, title: string) => void;
  onToggle: (id: string, isActive: boolean) => void;
}) {
  const { addToast } = useToast();
  const [uploadMode, setUploadMode] = useState<"youtube" | "file">("youtube");
  const [uploading, setUploading] = useState(false);

  function getYouTubeThumb(videoId: string) {
    return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
  }

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!form.title.trim()) {
      addToast("error", "Avval title kiriting");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("video", file);
      formData.append("title", form.title);
      if (form.description) formData.append("description", form.description);
      formData.append("sortOrder", String(form.sortOrder));
      const res = await client.post("/watch/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data?.success) {
        addToast("success", "Video yuklandi");
        onFormClose();
        setForm({ title: "", description: "", youtubeUrl: "", sortOrder: 0 });
      }
    } catch (err: any) {
      addToast("error", err?.response?.data?.error || "Video yuklashda xatolik");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const setForm = onFormChange;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-ds-text">Watch Videos</h3>
        <button onClick={onFormOpen} className="ds-btn-primary text-xs">
          + Add Video
        </button>
      </div>

      <p className="text-xs text-ds-text-muted">
        Barcha foydalanuvchilar /watch sahifasida ko'radi. YouTube link yoki fayl yuklang.
      </p>

      {formOpen && (
        <div className="ds-card p-4 space-y-3">
          <div className="flex items-center gap-2 border-b border-ds-border pb-3">
            <button
              onClick={() => setUploadMode("youtube")}
              className={`rounded px-3 py-1.5 text-xs font-medium transition-colors ${
                uploadMode === "youtube" ? "bg-ds-accent text-white" : "text-ds-text-muted hover:text-ds-text border border-ds-border"
              }`}
            >
              YouTube Link
            </button>
            <button
              onClick={() => setUploadMode("file")}
              className={`rounded px-3 py-1.5 text-xs font-medium transition-colors ${
                uploadMode === "file" ? "bg-ds-accent text-white" : "text-ds-text-muted hover:text-ds-text border border-ds-border"
              }`}
            >
              Fayldan Yuklash
            </button>
          </div>

          <div>
            <label className="block text-xs text-ds-text-dim mb-1">Title *</label>
            <input
              className="ds-input w-full text-xs"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Video title"
            />
          </div>
          <div>
            <label className="block text-xs text-ds-text-dim mb-1">Description</label>
            <input
              className="ds-input w-full text-xs"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Optional description"
            />
          </div>
          <div>
            <label className="block text-xs text-ds-text-dim mb-1">Sort Order</label>
            <input
              type="number"
              className="ds-input w-24 text-xs"
              value={form.sortOrder}
              onChange={(e) => setForm({ ...form, sortOrder: parseInt(e.target.value) || 0 })}
            />
          </div>

          {uploadMode === "youtube" ? (
            <>
              <div>
                <label className="block text-xs text-ds-text-dim mb-1">YouTube URL *</label>
                <input
                  className="ds-input w-full font-mono text-xs"
                  value={form.youtubeUrl}
                  onChange={(e) => setForm({ ...form, youtubeUrl: e.target.value })}
                  placeholder="https://youtube.com/watch?v=..."
                />
              </div>
              <div className="flex gap-2">
                <button onClick={onSubmit} disabled={saving} className="ds-btn-primary text-xs">
                  {saving ? "Saving..." : "Save"}
                </button>
                <button onClick={onFormClose} className="ds-btn-ghost text-xs border border-ds-border">
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="block text-xs text-ds-text-dim mb-1">Video fayl *</label>
                <p className="text-[10px] text-ds-text-muted mb-2">mp4, webm, ogg, mov — max 200MB</p>
                <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-ds-border bg-ds-card/50 px-4 py-6 transition-colors hover:border-ds-accent/40">
                  <svg className="h-5 w-5 text-ds-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                  </svg>
                  <span className="text-xs text-ds-text-dim">
                    {uploading ? "Yuklanmoqda..." : "Video tanlash"}
                  </span>
                  <input
                    type="file"
                    accept="video/mp4,video/webm,video/ogg,video/quicktime"
                    className="hidden"
                    onChange={handleFileUpload}
                    disabled={uploading}
                  />
                </label>
              </div>
              <div className="flex gap-2">
                <button onClick={onFormClose} className="ds-btn-ghost text-xs border border-ds-border">
                  Close
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {loading ? (
        <LoadingState />
      ) : videos.length === 0 ? (
        <EmptyState text="No watch videos yet" />
      ) : (
        <div className="space-y-2">
          {videos.map((v) => (
            <div key={v.id} className="ds-card flex items-center gap-4 p-4">
              {v.videoId ? (
                <img src={getYouTubeThumb(v.videoId)} alt="" className="h-16 w-28 shrink-0 rounded object-cover" />
              ) : v.fileUrl ? (
                <div className="flex h-16 w-28 shrink-0 items-center justify-center rounded bg-ds-surface">
                  <svg className="h-6 w-6 text-ds-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.348a1.125 1.125 0 010 1.971l-11.54 6.347a1.125 1.125 0 01-1.667-.985V5.653z" />
                  </svg>
                </div>
              ) : null}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-ds-text truncate">{v.title}</p>
                {v.description && (
                  <p className="mt-0.5 text-xs text-ds-text-muted truncate">{v.description}</p>
                )}
                <p className="mt-1 font-mono text-[10px] text-ds-text-muted truncate">
                  {v.videoId ? v.youtubeUrl : v.fileUrl || "N/A"}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="rounded bg-ds-surface px-1.5 py-0.5 text-[10px] text-ds-text-muted">
                  {v.videoId ? "YouTube" : v.fileUrl ? "File" : "N/A"}
                </span>
                <button
                  onClick={() => onToggle(v.id, v.isActive)}
                  className={`rounded px-2 py-1 text-[10px] font-medium transition-colors ${
                    v.isActive
                      ? "text-ds-success bg-ds-success/10 border border-ds-success/20"
                      : "text-ds-text-muted bg-ds-surface border border-ds-border"
                  }`}
                >
                  {v.isActive ? "ACTIVE" : "INACTIVE"}
                </button>
                <button
                  onClick={() => onDelete(v.id, v.title)}
                  className="rounded px-2 py-1 text-[10px] text-ds-danger hover:bg-ds-danger/10 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
